import type {
  ClassificationDefinition,
  Hit,
  ResolveTerms,
} from "$src/core/services/search-classification/types";
import {
  tagTerm,
  tagsFor,
} from "$src/core/services/search-classification/utils";

const definitionKey = (definition: ClassificationDefinition): string =>
  JSON.stringify(definition);

const addHit = (index: Map<string, Hit[]>, term: string, hit: Hit): void => {
  if (!term) return;
  const existing = index.get(term);
  if (!existing) {
    index.set(term, [hit]);
    return;
  }
  if (!existing.some((h) => h.label === hit.label)) existing.push(hit);
};

const stemWord = (word: string): string => {
  let stem = word.replace(/(ously|ous|ically|ic|ish)$/, "");

  if (stem.length > 4 && stem.endsWith("ies")) {
    stem = `${stem.slice(0, -3)}y`;
  } else if (stem.length > 4 && /(ss|sh|ch|x|z)es$/.test(stem)) {
    stem = stem.slice(0, -2);
  } else if (stem.length > 3 && stem.endsWith("s") && !stem.endsWith("ss")) {
    stem = stem.slice(0, -1);
  }

  if (stem.length > 5 && stem.endsWith("ful")) stem = stem.slice(0, -3);
  if (stem.length > 4 && stem.endsWith("e")) stem = stem.slice(0, -1);

  return stem;
};

const stemTerm = (term: string): string =>
  term.split(" ").map(stemWord).join(" ");

const MIN_FUZZY_LENGTH = 5;

const isOneEditApart = (a: string, b: string): boolean => {
  if (a === b) return true;

  if (a.length === b.length) {
    let first = -1;
    let second = -1;

    for (let index = 0; index < a.length; index++) {
      if (a[index] === b[index]) continue;
      if (first < 0) first = index;
      else if (second < 0) second = index;
      else return false;
    }

    if (second < 0) return true;
    return (
      second === first + 1 && a[first] === b[second] && a[second] === b[first]
    );
  }

  const [longer, shorter] = a.length > b.length ? [a, b] : [b, a];
  if (longer.length - shorter.length !== 1) return false;

  for (let index = 0; index < shorter.length; index++) {
    if (longer[index] === shorter[index]) continue;
    return longer.slice(index + 1) === shorter.slice(index);
  }

  return true;
};

const stemIndexCache = new Map<string, ReadonlyMap<string, Hit[]>>();

const stemIndexFor = (
  definition: ClassificationDefinition,
): ReadonlyMap<string, Hit[]> => {
  const key = definitionKey(definition);
  const cached = stemIndexCache.get(key);
  if (cached) return cached;

  const index = new Map<string, Hit[]>();
  for (const { kind, label } of tagsFor(definition)) {
    for (const part of [label, ...label.split("&")]) {
      addHit(index, stemTerm(tagTerm(part)), { kind, label, score: 1 });
    }
  }

  stemIndexCache.set(key, index);
  return index;
};

export const resolveWithTransformations =
  (definition: ClassificationDefinition): ResolveTerms =>
  async (terms) => {
    const index = stemIndexFor(definition);
    const hits = new Map<string, Hit[]>();

    for (const term of terms) {
      const key = stemTerm(term);

      const exact = index.get(key);
      if (exact) {
        hits.set(term, exact);
        continue;
      }

      if (key.length < MIN_FUZZY_LENGTH) continue;

      for (const [name, candidateHits] of index) {
        if (name.length < MIN_FUZZY_LENGTH) continue;
        if (!isOneEditApart(key, name)) continue;
        hits.set(term, candidateHits);
        break;
      }
    }

    return hits;
  };
